import * as React from "react";
import {useState, useCallback} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
   Button,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {RangeList} from "../../../components/rangelist/RangeList";
import {RangeListColumn, RangeListResponse} from "../../../components/rangelist/RangeListTypes";
import {ParamBrandHouse} from "../../../Parameters";
import {
   useAuditNotesPageQueryLazyQuery,
   useAuditNotesPageCreateMutation,
   AuditNotesPageQuery
} from "../../../Queries";
import {DateFormat} from "../../../utility/DateFormat";
import {setActiveAuditChange} from "../../../atoms/ActiveAuditChange";

type AuditNoteRow = NonNullable<NonNullable<AuditNotesPageQuery['AuditNotes']>[number]>;

export const AuditNotesPage = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   const navigate = useNavigate();
   const [dialogOpen, setDialogOpen] = useState(false);
   const [explanation, setExplanation] = useState("");
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [refetchKey, setRefetchKey] = useState(0);

   const [fetchAuditNotes] = useAuditNotesPageQueryLazyQuery({
      fetchPolicy: "no-cache"
   });

   const [createAuditNote] = useAuditNotesPageCreateMutation();

   const columns: RangeListColumn<AuditNoteRow>[] = [
      {
         id: "createdAt",
         label: "Date",
         width: 2,
         get: (row) => row.createdAt,
         render: (data) => {
            if (!data) return "-";
            return DateFormat.toReadable(data);
         }
      },
      {
         id: "explanation",
         label: "Explanation",
         field: "explanation",
         width: 8,
         ellipsis: true
      }
   ];

   const onRequestData = useCallback(async (
      _search: string | undefined,
      _sort: string | undefined,
      _page: number,
      _perPage: number,
      _filter?: any
   ): Promise<RangeListResponse<AuditNoteRow>> => {
      const {data} = await fetchAuditNotes({
         variables: {
            brandHouseId: brandHouseId!
         }
      });

      const allItems = (data?.AuditNotes || []).filter(Boolean) as AuditNoteRow[];

      // Sort by createdAt descending (newest first)
      allItems.sort((a, b) => {
         const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
         const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
         return dateB - dateA;
      });

      return {
         data: allItems,
         totalCount: allItems.length
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [brandHouseId, fetchAuditNotes, refetchKey]);

   const handleOpenDialog = () => {
      setDialogOpen(true);
      setExplanation("");
   };

   const handleCloseDialog = () => {
      setDialogOpen(false);
      setExplanation("");
   };

   const handleCreate = async () => {
      if (!explanation.trim()) return;

      setIsSubmitting(true);
      try {
         const result = await createAuditNote({
            variables: {
               brandHouseId: brandHouseId!,
               explanation: explanation.trim()
            }
         });

         const createdId = result.data?.AuditNoteCreate?._id;
         if (createdId) {
            setActiveAuditChange({
               _id: createdId,
               explanation: explanation.trim(),
               brandHouseId: brandHouseId!
            });
         }

         handleCloseDialog();
         setRefetchKey(prev => prev + 1);
      } catch (error) {
         console.error("Failed to create audit change:", error);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Page>
         <PageHeader title={"Audit Change"}>
            <Button
               variant="contained"
               startIcon={<Add />}
               onClick={handleOpenDialog}
            >
               Create
            </Button>
         </PageHeader>
         <PageContent>
            <RangeList
               key={refetchKey}
               globalId={"auditNotesList"}
               columns={columns}
               itemsPerPage={50}
               onRequestData={onRequestData}
               onSelectRow={(row) => {
                  navigate(`/brands/${brandHouseId}/audits/${row._id}`);
               }}
               options={{
                  idField: "_id"
               }}
            />
         </PageContent>

         <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
         >
            <DialogTitle>Create Audit Change</DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  margin="dense"
                  label="Explanation"
                  fullWidth
                  multiline
                  rows={4}
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder="Enter the explanation for this audit change..."
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                  Cancel
               </Button>
               <Button
                  variant="contained"
                  onClick={handleCreate}
                  disabled={!explanation.trim() || isSubmitting}
               >
                  {isSubmitting ? "Creating..." : "Create"}
               </Button>
            </DialogActions>
         </Dialog>
      </Page>
   );
};
